import { InputProps } from "@/src/types/input.type";
import { useMemo, useState } from "react";
import Dropdown from "react-bootstrap/esm/Dropdown";
import Form from "react-bootstrap/esm/Form";
import InputGroup from "react-bootstrap/esm/InputGroup";

const pad = (value: number) => String(value).padStart(2, "0");

const parseDate = (value?: string) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : undefined;
};

const toDateValue = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const monthLabel = new Intl.DateTimeFormat("en", { month: "long", year: "numeric" });
const weekdayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function DateInput({ className, value, readOnly=false, onChanged,onBlur }: Omit<InputProps, "label" | "labelClassName" | "options" | "type">) {
  const selectedDate = parseDate(value);
  const [isOpen, setIsOpen] = useState(false);
  const [shownMonth, setShownMonth] = useState(() => {
    const date = selectedDate ?? new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  const calendarDays = useMemo(() => {
    const firstWeekday = shownMonth.getDay();
    const dayCount = new Date(shownMonth.getFullYear(), shownMonth.getMonth() + 1, 0).getDate();
    return Array.from({ length: Math.ceil((firstWeekday + dayCount) / 7) * 7 }, (_, index) => {
      const day = index - firstWeekday + 1;
      return day > 0 && day <= dayCount ? new Date(shownMonth.getFullYear(), shownMonth.getMonth(), day) : undefined;
    });
  }, [shownMonth]);

  const selectDate = (date: Date) => {
    onChanged?.(toDateValue(date));
    setIsOpen(false);
  };

  return (
    <InputGroup className="w-auto d-flex align-items-stretch">
      <Form.Control
        className={[className?.replace(/form-control\s+/g, ""), "flex-grow-1", "h-100"].filter(Boolean).join(" ")}
        value={value ?? ""}
        onBlur={onBlur}
        readOnly
        onKeyDown={(event) => {
          if (event.key === " " || event.key === "Spacebar") {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
      />
      <Dropdown
        align="end"
        autoClose="outside"
        show={isOpen}
        onToggle={(nextShow) => {
          if (nextShow && selectedDate) {
            setShownMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
          }
          setIsOpen(nextShow);
        }}
      >
        <Dropdown.Toggle
          className="flex-shrink-0 d-flex align-items-center justify-content-center px-1 py-0"
          variant="outline-secondary"
          disabled={readOnly}
          aria-label="Choose date"
          style={{ maxHeight: "calc(1.5em + 0.75rem + 2px)" }}
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === "Tab") {
              event.preventDefault();
            }
          }}
        >
          <span aria-hidden="true">&#128197;</span>
        </Dropdown.Toggle>
        <Dropdown.Menu className="p-2 shadow" style={{ minWidth: "17rem" }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <button
              type="button"
              className="btn btn-sm btn-light"
              aria-label="Previous month"
              onClick={() => setShownMonth((month) => new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            >
              &#8249;
            </button>
            <strong className="small">{monthLabel.format(shownMonth)}</strong>
            <button
              type="button"
              className="btn btn-sm btn-light"
              aria-label="Next month"
              onClick={() => setShownMonth((month) => new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            >
              &#8250;
            </button>
          </div>
          <div className="d-grid text-center small" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: "0.15rem" }}>
            {weekdayLabels.map((day) => <span className="text-secondary fw-semibold py-1" key={day}>{day}</span>)}
            {calendarDays.map((date, index) => {
              const isSelected = date && selectedDate && toDateValue(date) === toDateValue(selectedDate);
              const isToday = date && toDateValue(date) === toDateValue(new Date());
              return date ? (
                <button
                  type="button"
                  key={toDateValue(date)}
                  className={`btn btn-sm ${isSelected ? "btn-primary" : "btn-light"} ${isToday && !isSelected ? "border-primary" : ""}`}
                  onClick={() => selectDate(date)}
                >
                  {date.getDate()}
                </button>
              ) : <span key={`blank-${index}`} />;
            })}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </InputGroup>
  );
}